// @ts-strict-ignore
import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { InputMaskModule } from "primeng/inputmask";
import { InputNumberModule } from "primeng/inputnumber";
import { InputTextModule } from "primeng/inputtext";
import { RadioButtonModule } from "primeng/radiobutton";
import { TableModule } from "primeng/table";
import { ErrorCheck } from "src/app/shared/models/error-check.model";
import { ProgressCheck } from "src/app/shared/models/progress-check.model";
import { SharedDirectivesModule } from "src/app/shared/shared-directives.module";
import { ErrorMessageComponent } from "src/app/modules/esim/shared/components/error-message/error-message.component";
import * as C from "../../../../../../shared/constants/globals.constants";
import { EWizDynamicBaseClassComponent } from "src/app/modules/esim/shared/classes/ewiz-dynamic.class";
import { DataService } from "src/app/shared/services/data.service";
import * as ProfileModel from "src/app/shared/models/profile.model";

@Component({
  selector: C.EWIZ_PIN_CONFIGURATION,
  templateUrl: "./pin-configuration.component.html",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputMaskModule,
    InputNumberModule,
    InputTextModule,
    RadioButtonModule,
    TableModule,
    SharedDirectivesModule,
    ErrorMessageComponent
  ]
})
export class EWizPinConfigurationComponent extends EWizDynamicBaseClassComponent implements OnInit, ErrorCheck, ProgressCheck {
  constructor(private dataService: DataService) {
    super();
  }

  selectedPins: ProfileModel.PinDto[] = [];
  nextId = 0;

  ngOnInit() {
    this.profileValues.pinsVisited = true;

    this.errorCheck(this.profileValues, this.dataService);
    this.handleFormEvents();

    if (this.profileValues.pins.length > 0) {
      for (const pin of this.profileValues.pins) {
        if (pin.id > this.nextId) {
          this.nextId = pin.id;
        }
      }
    }
  }

  errorCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    // Always reset error values to default state prior to executing check
    this.errorFound = false;
    this.errorMessage = "";

    // All PIN numbers must be within range
    for (const pin of values.pins) {
      if (+pin.type === 1) {
        if (pin.number && !dataService.valueInRange(pin.number, 1, 8)) {
          this.errorMessage = $localize`PIN numbers must be within a range of 1 and 8`;
          this.errorFound = true;
        }
      } else if (+pin.type === 2) {
        if (pin.number && !dataService.valueInRange(pin.number, 1, 8)) {
          this.errorMessage = $localize`Local PIN numbers must be within a range of 1 and 8`;
          this.errorFound = true;
        }
      } else if (+pin.type === 3) {
        if (pin.number && !dataService.valueInRange(pin.number, 1, 10)) {
          this.errorMessage = $localize`ADM numbers must be within a range of 1 and 10`;
          this.errorFound = true;
        }
      }
    }

    // All PIN numbers must be unique within their types
    const pinNumbers = [];
    const localpinNumbers = [];
    const admNumbers = [];

    for (const pin of values.pins) {
      if (pin.number) {
        if (+pin.type === 1) {
          pinNumbers.push(+pin.number);
        } else if (+pin.type === 2) {
          localpinNumbers.push(+pin.number);
        } else if (+pin.type === 3) {
          admNumbers.push(+pin.number);
        }
      }
    }

    if (dataService.arrayContainDuplicates(pinNumbers)) {
      this.errorMessage = $localize`All PIN numbers must be unique`;
      this.errorFound = true;
    } else if (dataService.arrayContainDuplicates(localpinNumbers)) {
      this.errorMessage = $localize`All Local PIN numbers must be unique`;
      this.errorFound = true;
    } else if (dataService.arrayContainDuplicates(admNumbers)) {
      this.errorMessage = $localize`All ADM numbers must be unique`;
      this.errorFound = true;
    }

    // All PIN sizes must be entered and within range
    for (const pin of values.pins) {
      if (+pin.type === 1 || +pin.type === 2) {
        if (dataService.isNullOrEmpty(pin.size)) {
          this.errorMessage = $localize`PIN Size values must be entered`;
          this.errorFound = true;
        } else if (!dataService.valueInRange(pin.size, 4, 8)) {
          this.errorMessage = $localize`PIN Size values must be within a range of 4 and 8`;
          this.errorFound = true;
        }
      } else if (+pin.type === 3) {
        if (pin.size !== 8 && pin.size !== 16) {
          this.errorMessage = $localize`ADM Size values must be set to digits or HEX chars`;
          this.errorFound = true;
        }
      }
    }

    // All PIN Values must have a length equal to the PIN Size
    for (const pin of values.pins) {
      if (+pin.valueGeneration === 2 && pin.size && pin.value && `${pin.value}`.replace(/_/g, "").length !== +pin.size) {
        this.errorMessage = $localize`PIN Value lengths must match their specified PIN Size`;
        this.errorFound = true;
      }
    }

    // Additional conditions that only apply to PIN type 1/2
    for (const pin of values.pins) {
      if (+pin.type === 1 || +pin.type === 2) {
        // All Max Attempts must be entered and within range
        if (dataService.isNullOrEmpty(pin.maxAttempts)) {
          this.errorMessage = $localize`Max Attempt values must be entered`;
          this.errorFound = true;
        } else if (!dataService.valueInRange(pin.maxAttempts, 0, 15)) {
          this.errorMessage = $localize`Max Attempt values must be within a range of 0 and 15`;
          this.errorFound = true;
        }

        // All Retries Left must be entered and within range
        if (dataService.isNullOrEmpty(pin.retryNumberLeft)) {
          this.errorMessage = $localize`Retries Left values must be entered`;
          this.errorFound = true;
        } else if (!dataService.valueInRange(pin.retryNumberLeft, 0, 15)) {
          this.errorMessage = $localize`Retries Left values must be within a range of 0 and 15`;
          this.errorFound = true;
        } else if (!dataService.isNullOrEmpty(pin.maxAttempts) && +pin.retryNumberLeft > +pin.maxAttempts) {
          this.errorMessage = $localize`Retries Left values must not exceed their Max Attempts`;
          this.errorFound = true;
        }
      }
    }

    return this.errorFound;
  }

  progressCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    // All PIN values (if applicable) and value generation settings must be entered
    for (const pin of values.pins) {
      if (dataService.isNullOrEmpty(pin.valueGeneration)) {
        return false;
      } else if (+pin.valueGeneration === 2 && dataService.isNullOrEmpty(pin.value)) {
        return false;
      }
    }

    // All PIN numbers must be entered and within range
    for (const pin of values.pins) {
      if (dataService.isNullOrEmpty(pin.number)) {
        return false;
      }
    }

    // Need at least one PIN and one ADM
    let pinFound = false;
    let admFound = false;

    for (const pin of values.pins) {
      if (+pin.type === 1) {
        pinFound = true;
      } else if (+pin.type === 3) {
        admFound = true;
      }
    }

    if (!pinFound || !admFound) {
      return false;
    }

    if (!values.pinsVisited) {
      return false;
    }

    return true;
  }

  generatePinValue(pinObject) {
    pinObject.value = this.getRandomPinBySize(pinObject.size);

    this.handleFormEvents();
  }

  getRandomPinBySize(size) {
    let newPin = "";

    if (size) {
      for (let i = 0, ix = +size; i < ix; i++) {
        newPin += this.dataService.generateRandomInteger(0, 9);
      }
    }

    return newPin;
  }

  addPin() {
    const newPins = [...this.profileValues.pins];

    newPins.push({
      id: ++this.nextId,
      type: "1",
      number: null,
      size: 4,
      value: null,
      valueGeneration: "1",
      attributes: {
        stateChange: "1",
        pinChange: "1",
        pinEnabled: "2"
      },
      maxAttempts: 10,
      retryNumberLeft: 10,
      hasPuk: true,
      puk: {
        size: 8,
        value: null,
        valueGeneration: "1",
        maxAttempts: 10,
        retryNumberLeft: 10
      }
    });

    this.profileValues.pins = [...newPins];

    this.handleFormEvents();
  }

  addLocalPin() {
    const newPins = [...this.profileValues.pins];

    newPins.push({
      id: ++this.nextId,
      type: "2",
      number: null,
      size: 4,
      value: null,
      valueGeneration: "1",
      attributes: {
        stateChange: "1",
        pinChange: "1",
        pinEnabled: "2"
      },
      maxAttempts: 10,
      retryNumberLeft: 10,
      hasPuk: true,
      puk: {
        size: 8,
        value: null,
        valueGeneration: "1",
        maxAttempts: 10,
        retryNumberLeft: 10
      }
    });

    this.profileValues.pins = [...newPins];

    this.handleFormEvents();
  }

  addAdm() {
    const newPins = [...this.profileValues.pins];

    newPins.push({
      id: ++this.nextId,
      type: "3",
      number: null,
      size: 8,
      value: null,
      valueGeneration: "1",
      hasPuk: false
    });

    this.profileValues.pins = [...newPins];

    this.handleFormEvents();
  }

  deletePins() {
    const newPins = [];

    for (const originalPin of this.profileValues.pins) {
      let removePins = false;

      for (const selectedPin of this.selectedPins) {
        if (originalPin.id === selectedPin.id) {
          removePins = true;
          break;
        }
      }

      if (!removePins) {
        newPins.push(originalPin);
      }
    }

    this.profileValues.pins = [...newPins];
    this.selectedPins = [];

    this.handleFormEvents();
  }

  parseAdmPinValue(pin: ProfileModel.PinDto) {
    if (pin.value) {
      pin.value = pin.value.replace(/[^0-9]*/g, "");
    }
  }
}
